/**
 * PropertyMapView - Interactive Map Component for Property Search
 * 
 * Features:
 * - Mapbox GL JS with custom styling
 * - Property markers with clustering
 * - Draw polygon to search area
 * - Property popup details
 * - Current location button
 * - Synced with search filters
 * - Save search from map view
 */
import React, { useRef, useEffect, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Navigation,
  Maximize2,
  Minimize2,
  Layers,
  Pencil,
  Trash2,
  X,
  MapPin,
  Bed,
  Bath,
  PoundSterling,
  Heart,
  ExternalLink,
  Loader2,
  Search,
  Save,
  Home,
} from 'lucide-react';
import { cn, formatCurrency } from '../../lib/utils';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';

import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';

// Mapbox access token - LetWise production token
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || 'pk.eyJ1IjoibGV0d2lzZSIsImEiOiJjbWtjbDhncTQwMXp5M2NzZmVmNjdxcHoxIn0.I2ozcsJKI_Rglf7caLEKvA';
mapboxgl.accessToken = MAPBOX_TOKEN;

// UK center coordinates
const UK_CENTER = [-1.5, 53.5];
const DEFAULT_ZOOM = 6;

// =============================================================================
// PROPERTY POPUP COMPONENT
// =============================================================================

const PropertyPopup = ({ property, onClose, onFavorite, isFavorite }) => {
  const addr = property.address || {};
  
  return (
    <div className="w-72 max-w-full" data-testid="map-property-popup">
      {/* Image */}
      {property.image && (
        <div className="h-32 -mx-3 -mt-3 mb-3 overflow-hidden rounded-t-lg">
          <img 
            src={property.image} 
            alt={property.title}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      
      {/* Content */}
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-sm line-clamp-2">{property.title}</h3>
          <button
            onClick={() => onFavorite?.(property.id)}
            className={cn(
              "p-1 rounded-full flex-shrink-0",
              isFavorite ? "text-red-500" : "text-gray-400 hover:text-red-500"
            )}
          >
            <Heart className={cn("w-4 h-4", isFavorite && "fill-current")} />
          </button>
        </div>
        
        <p className="text-lg font-bold text-blue-600">
          {formatCurrency(property.rent_amount)}<span className="text-xs text-gray-500 font-normal">/pcm</span>
        </p>
        
        <p className="text-xs text-gray-500 flex items-center gap-1">
          <MapPin className="w-3 h-3" />
          {[addr.city, addr.postcode].filter(Boolean).join(', ') || 'Location TBD'}
        </p>
        
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <Bed className="w-3 h-3" />
            {property.bedrooms || '-'}
          </span>
          <span className="flex items-center gap-1">
            <Bath className="w-3 h-3" />
            {property.bathrooms || '-'}
          </span>
          <span className="flex items-center gap-1">
            <Home className="w-3 h-3" />
            {property.property_type || 'Property'}
          </span>
        </div>
        
        <div className="flex gap-1 flex-wrap">
          {property.pets_allowed && <Badge variant="outline" className="text-xs">Pets OK</Badge>}
          {property.parking && <Badge variant="outline" className="text-xs">Parking</Badge>}
        </div>
        
        <Link
          to={`/properties/${property.id}`}
          className="mt-2 w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          View Details
          <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
};


// =============================================================================
// MAIN MAP COMPONENT
// =============================================================================

const PropertyMapView = forwardRef(({ 
  filters = {},
  onFiltersChange,
  onSaveSearch,
  favorites = new Set(),
  onFavorite,
  isAuthenticated = false,
  className,
  initialCenter,
  initialZoom,
}, ref) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const popupRef = useRef(null);
  const markersRef = useRef([]);
  const loadPropertiesInViewRef = useRef(null);
  const searchInPolygonRef = useRef(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [mapStyle, setMapStyle] = useState('streets');
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawnArea, setDrawnArea] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  // Map styles
  const MAP_STYLES = {
    streets: 'mapbox://styles/mapbox/streets-v12',
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
    light: 'mapbox://styles/mapbox/light-v11',
    dark: 'mapbox://styles/mapbox/dark-v11',
  };

  // Expose methods to parent
  useImperativeHandle(ref, () => ({
    flyTo: (center, zoom = 14) => {
      if (map.current) {
        map.current.flyTo({ center, zoom, duration: 1500 });
      }
    },
    setProperties: (props) => setProperties(props),
    clearDrawnArea: () => {
      if (draw.current) {
        draw.current.deleteAll();
        setDrawnArea(null);
      }
    },
    getMapBounds: () => {
      if (map.current) {
        const bounds = map.current.getBounds();
        return {
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest(),
        };
      }
      return null;
    }
  }));

  // Initialize map - runs once on mount
  useEffect(() => {
    if (map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: MAP_STYLES[mapStyle],
      center: initialCenter || UK_CENTER,
      zoom: initialZoom || DEFAULT_ZOOM,
      attributionControl: false,
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');
    map.current.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

    // Add geolocate control
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
    });
    map.current.addControl(geolocate, 'top-right');

    // Initialize draw control
    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      defaultMode: 'simple_select',
      styles: [
        // Polygon fill
        {
          id: 'gl-draw-polygon-fill',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon']],
          paint: {
            'fill-color': '#3b82f6',
            'fill-opacity': 0.15,
          },
        },
        // Polygon stroke
        {
          id: 'gl-draw-polygon-stroke',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round',
          },
          paint: {
            'line-color': '#3b82f6',
            'line-width': 2,
          },
        },
        // Vertex points
        {
          id: 'gl-draw-polygon-and-line-vertex',
          type: 'circle',
          filter: ['all', ['==', 'meta', 'vertex']],
          paint: {
            'circle-radius': 6,
            'circle-color': '#fff',
            'circle-stroke-color': '#3b82f6',
            'circle-stroke-width': 2,
          },
        },
      ],
    });
    map.current.addControl(draw.current);

    // Map load event
    map.current.on('load', () => {
      setMapLoaded(true);
      
      // Add cluster source
      map.current.addSource('properties', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
      });

      // Cluster circles
      map.current.addLayer({
        id: 'clusters',
        type: 'circle',
        source: 'properties',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step',
            ['get', 'point_count'],
            '#3b82f6', // < 10: blue
            10,
            '#8b5cf6', // 10-30: purple
            30,
            '#ec4899', // 30+: pink
          ],
          'circle-radius': [
            'step',
            ['get', 'point_count'],
            20, // < 10
            10,
            25, // 10-30
            30,
            30, // 30+
          ],
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
      });

      // Cluster count labels
      map.current.addLayer({
        id: 'cluster-count',
        type: 'symbol',
        source: 'properties',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12,
        },
        paint: {
          'text-color': '#ffffff',
        },
      });

      // Individual property points
      map.current.addLayer({
        id: 'unclustered-point',
        type: 'circle',
        source: 'properties',
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': '#3b82f6',
          'circle-radius': 8,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#fff',
        },
      });

      // Click on cluster to zoom
      map.current.on('click', 'clusters', (e) => {
        const features = map.current.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        });
        const clusterId = features[0].properties.cluster_id;
        map.current.getSource('properties').getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.current.easeTo({
            center: features[0].geometry.coordinates,
            zoom: zoom,
          });
        });
      });

      // Click on property point to show popup
      map.current.on('click', 'unclustered-point', (e) => {
        const feature = e.features[0];
        const coordinates = feature.geometry.coordinates.slice();
        const props = feature.properties;
        
        // Parse nested JSON
        const address = typeof props.address === 'string' ? JSON.parse(props.address) : props.address;
        
        setSelectedProperty({
          ...props,
          address,
          coordinates,
        });
      });

      // Change cursor on hover
      map.current.on('mouseenter', 'clusters', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'clusters', () => {
        map.current.getCanvas().style.cursor = '';
      });
      map.current.on('mouseenter', 'unclustered-point', () => {
        map.current.getCanvas().style.cursor = 'pointer';
      });
      map.current.on('mouseleave', 'unclustered-point', () => {
        map.current.getCanvas().style.cursor = '';
      });
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update map style
  useEffect(() => {
    if (map.current && mapLoaded) {
      map.current.setStyle(MAP_STYLES[mapStyle]);
    }
    // MAP_STYLES is a static constant defined outside the component
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapStyle, mapLoaded]);

  // Setup draw event handlers after map loads
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const onDrawCreate = () => {
      const data = draw.current.getAll();
      if (data.features.length > 0) {
        const polygon = data.features[0];
        setDrawnArea(polygon);
        setIsDrawing(false);
        // Trigger search with polygon coordinates
        const coords = polygon.geometry.coordinates[0];
        searchInPolygonRef.current(coords);
      }
    };

    map.current.on('draw.create', onDrawCreate);
    map.current.on('draw.update', onDrawCreate);
    map.current.on('draw.delete', () => setDrawnArea(null));

    return () => {
      if (map.current) {
        map.current.off('draw.create', onDrawCreate);
        map.current.off('draw.update', onDrawCreate);
        map.current.off('draw.delete', () => setDrawnArea(null));
      }
    };
  }, [mapLoaded]);

  // Load properties on map move
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    const onMoveEnd = () => {
      if (!isDrawing && !drawnArea) {
        loadPropertiesInViewRef.current();
      }
    };

    map.current.on('moveend', onMoveEnd);

    // Initial load
    loadPropertiesInViewRef.current();

    return () => {
      if (map.current) {
        map.current.off('moveend', onMoveEnd);
      }
    };
  }, [mapLoaded, isDrawing, drawnArea]);

  // Update properties on map when data changes
  useEffect(() => {
    if (map.current && mapLoaded && map.current.getSource('properties')) {
      const geojson = {
        type: 'FeatureCollection',
        features: properties.map((p) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: p.geometry?.coordinates || [p.longitude, p.latitude],
          },
          properties: p.properties || p,
        })),
      };
      map.current.getSource('properties').setData(geojson);
    }
  }, [properties, mapLoaded]);

  // Reload when filters change
  useEffect(() => {
    if (mapLoaded && !drawnArea) {
      loadPropertiesInViewRef.current?.();
    }
  }, [filters, mapLoaded, drawnArea]);

  // Load properties in current view
  const loadPropertiesInView = useCallback(async () => {
    if (!map.current || isLoading) return;

    setIsLoading(true);
    try {
      const bounds = map.current.getBounds();
      const response = await api.post('/listings/public/search/bounds', {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
        filters: {
          property_type: filters.propertyType,
          bedrooms: filters.bedrooms,
          min_rent: filters.minPrice,
          max_rent: filters.maxPrice,
          amenities: filters.amenities,
        },
      });

      setProperties(response.data.features || []);
    } catch (error) {
      console.error('Failed to load properties:', error);
      // Don't show toast on every map move
    } finally {
      setIsLoading(false);
    }
  }, [filters, isLoading]);

  // Search in drawn polygon
  const searchInPolygon = useCallback(async (coordinates) => {
    setIsLoading(true);
    try {
      const response = await api.post('/listings/public/search/polygon', {
        coordinates,
        filters: {
          property_type: filters.propertyType,
          bedrooms: filters.bedrooms,
          min_rent: filters.minPrice,
          max_rent: filters.maxPrice,
          amenities: filters.amenities,
        },
      });

      setProperties(response.data.features || []);
      toast.success(`Found ${response.data.total} properties in selected area`);
    } catch (error) {
      console.error('Polygon search failed:', error);
      toast.error('Failed to search in selected area');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  // Keep refs updated with latest function versions
  useEffect(() => {
    loadPropertiesInViewRef.current = loadPropertiesInView;
    searchInPolygonRef.current = searchInPolygon;
  }, [loadPropertiesInView, searchInPolygon]);

  // Toggle draw mode
  const handleToggleDraw = () => {
    if (isDrawing) {
      draw.current.changeMode('simple_select');
      setIsDrawing(false);
    } else {
      draw.current.deleteAll();
      setDrawnArea(null);
      draw.current.changeMode('draw_polygon');
      setIsDrawing(true);
      toast.info('Click on the map to draw a search area');
    }
  };

  // Clear drawn area
  const handleClearArea = () => {
    draw.current.deleteAll();
    setDrawnArea(null);
    setIsDrawing(false);
    loadPropertiesInView();
  };

  // Toggle fullscreen
  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
    setTimeout(() => {
      map.current?.resize();
    }, 100);
  };

  // Save current search
  const handleSaveSearch = () => {
    if (!isAuthenticated) {
      toast.error('Please login to save searches');
      return;
    }

    const searchCriteria = {
      ...filters,
      bounds: map.current ? {
        north: map.current.getBounds().getNorth(),
        south: map.current.getBounds().getSouth(),
        east: map.current.getBounds().getEast(),
        west: map.current.getBounds().getWest(),
        center: map.current.getCenter().toArray(),
        zoom: map.current.getZoom(),
      } : null,
      polygon: drawnArea?.geometry?.coordinates?.[0] || null,
    };

    onSaveSearch?.(searchCriteria);
  };

  // Current location
  const handleGoToCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          map.current?.flyTo({
            center: [position.coords.longitude, position.coords.latitude],
            zoom: 13,
            duration: 1500,
          });
        },
        () => {
          toast.error('Unable to get your location');
        }
      );
    }
  };

  // Close popup
  const handleClosePopup = () => {
    setSelectedProperty(null);
  };

  return (
    <div 
      className={cn(
        "relative rounded-lg overflow-hidden border bg-gray-100",
        isFullscreen && "fixed inset-0 z-50 rounded-none",
        className
      )}
      data-testid="property-map-view"
    >
      {/* Map Container */}
      <div ref={mapContainer} className="w-full h-full min-h-[400px]" />

      {/* Loading Indicator */}
      {isLoading && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 z-10">
          <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
          <span className="text-sm">Loading properties...</span>
        </div>
      )}

      {/* Map Controls */}
      <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
        {/* Draw Area Button */}
        <Button
          variant={isDrawing ? "default" : "outline"}
          size="sm"
          onClick={handleToggleDraw}
          className="bg-white shadow-md hover:bg-gray-50"
          data-testid="map-draw-btn"
        >
          <Pencil className="w-4 h-4 mr-2" />
          {isDrawing ? 'Drawing...' : 'Draw Area'}
        </Button>

        {/* Clear Drawn Area */}
        {drawnArea && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearArea}
            className="bg-white shadow-md hover:bg-gray-50"
            data-testid="map-clear-area-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear Area
          </Button>
        )}

        {/* Save Search Button */}
        {isAuthenticated && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleSaveSearch}
            className="bg-white shadow-md hover:bg-gray-50"
            data-testid="map-save-search-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            Save Search
          </Button>
        )}
      </div>

      {/* Bottom Left Controls */}
      <div className="absolute bottom-4 left-4 flex gap-2 z-10">
        {/* Map Style Toggle */}
        <div className="bg-white rounded-md shadow-md overflow-hidden flex">
          {Object.keys(MAP_STYLES).map((style) => (
            <button
              key={style}
              onClick={() => setMapStyle(style)}
              className={cn(
                "px-3 py-2 text-xs capitalize transition-colors",
                mapStyle === style 
                  ? "bg-blue-600 text-white" 
                  : "hover:bg-gray-100"
              )}
            >
              {style}
            </button>
          ))}
        </div>
      </div>

      {/* Bottom Right Controls */}
      <div className="absolute bottom-4 right-4 flex flex-col gap-2 z-10">
        {/* Current Location */}
        <Button
          variant="outline"
          size="icon"
          onClick={handleGoToCurrentLocation}
          className="bg-white shadow-md hover:bg-gray-50"
          data-testid="map-location-btn"
        >
          <Navigation className="w-4 h-4" />
        </Button>

        {/* Fullscreen Toggle */}
        <Button
          variant="outline"
          size="icon"
          onClick={handleToggleFullscreen}
          className="bg-white shadow-md hover:bg-gray-50"
          data-testid="map-fullscreen-btn"
        >
          {isFullscreen ? (
            <Minimize2 className="w-4 h-4" />
          ) : (
            <Maximize2 className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Property Count Badge */}
      <div className="absolute top-4 right-16 z-10">
        <Badge className="bg-white text-gray-700 shadow-md">
          {properties.length} properties
        </Badge>
      </div>

      {/* Property Popup */}
      {selectedProperty && (
        <div 
          className="absolute bottom-20 left-1/2 -translate-x-1/2 z-20 w-80 max-w-[calc(100%-2rem)]"
          data-testid="map-popup"
        >
          <Card className="shadow-lg">
            <button
              onClick={handleClosePopup}
              className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100 z-10"
            >
              <X className="w-4 h-4" />
            </button>
            <CardContent className="p-3">
              <PropertyPopup
                property={selectedProperty}
                onClose={handleClosePopup}
                onFavorite={onFavorite}
                isFavorite={favorites.has(selectedProperty.id)}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Fullscreen Close Button */}
      {isFullscreen && (
        <Button
          variant="outline"
          size="icon"
          onClick={handleToggleFullscreen}
          className="absolute top-4 right-4 bg-white shadow-md z-20"
        >
          <X className="w-5 h-5" />
        </Button>
      )}

      {/* Draw Mode Indicator */}
      {isDrawing && (
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 z-10 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg text-sm">
          Click to add points • Double-click to finish
        </div>
      )}
    </div>
  );
});

PropertyMapView.displayName = 'PropertyMapView';

export default PropertyMapView;
